import { useEffect, useState } from 'react';

const DISMISS_KEY = 'sp_install_dismissed_v1';

function isStandalone() {
  // PWA installed?
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isMobile() {
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  if (!isIOS()) return false;
  // exclude Chrome/Firefox/etc on iOS (Add to Home Screen only works in Safari)
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android beforeinstallprompt event
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(DISMISS_KEY));

  useEffect(() => {
    if (dismissed || isStandalone() || !isMobile()) return;

    // Android Chrome / Edge — capture the beforeinstallprompt event
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // iOS Safari — no native event, show custom hint after a delay
    if (isIOSSafari()) {
      const t = setTimeout(() => setIosHint(true), 2000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBefore);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBefore);
  }, [dismissed]);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) {}
    setDeferred(null);
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
    setDeferred(null);
    setIosHint(false);
  };

  if (dismissed || isStandalone() || !isMobile()) return null;
  if (!deferred && !iosHint) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: 'calc(12px + env(safe-area-inset-left))',
        right: 'calc(12px + env(safe-area-inset-right))',
        bottom: 'calc(12px + env(safe-area-inset-bottom))',
        zIndex: 50,
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--acc)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-card)',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        fontSize: '12px',
        color: 'var(--ink)',
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          flexShrink: 0,
          borderRadius: '8px',
          background: 'var(--acc)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize: '18px',
        }}
      >
        S
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 2px' }}>
          Zainstaluj ScanPilots
        </p>
        {deferred ? (
          <p style={{ fontSize: '11px', color: 'var(--ink2)', margin: 0, lineHeight: 1.45 }}>
            Dodaj aplikację do ekranu głównego — szybszy dostęp, tryb pełnoekranowy.
          </p>
        ) : (
          <p style={{ fontSize: '11px', color: 'var(--ink2)', margin: 0, lineHeight: 1.45 }}>
            Dotknij ikony udostępniania w pasku Safari, a następnie wybierz <strong style={{ color: 'var(--ink)' }}>Do ekranu początkowego</strong>.
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          {deferred && (
            <button
              onClick={handleInstall}
              style={{
                background: 'var(--acc)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                padding: '6px 12px',
                cursor: 'pointer',
              }}
            >
              Zainstaluj
            </button>
          )}
          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--ink2)',
              fontSize: '11px',
              fontWeight: 600,
              padding: '6px 12px',
              cursor: 'pointer',
            }}
          >
            Może później
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Zamknij"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--ink3)',
          fontSize: '16px',
          fontWeight: 700,
          cursor: 'pointer',
          padding: '0',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
